import { ReactNode } from 'react';
import { useAppSelector } from '../../store';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '../../config/permissions';
import type { Permission } from '../../types';

interface PermissionGuardProps {
  children: ReactNode;
  permission?: Permission;
  anyPermissions?: Permission[];
  allPermissions?: Permission[];
  fallback?: ReactNode;
  showFallback?: boolean;
}

/**
 * PermissionGuard component - Conditionally renders children based on user permissions
 * 
 * Usage:
 * <PermissionGuard permission="incidents:create">
 *   <CreateIncidentButton />
 * </PermissionGuard>
 * 
 * <PermissionGuard anyPermissions={["incidents:edit", "incidents:delete"]}>
 *   <EditControls />
 * </PermissionGuard>
 * 
 * <PermissionGuard allPermissions={["incidents:view", "map:view"]} fallback={<AccessDenied />}>
 *   <IncidentMap />
 * </PermissionGuard>
 */
export default function PermissionGuard({
  children,
  permission,
  anyPermissions,
  allPermissions,
  fallback = null,
  showFallback = true,
}: PermissionGuardProps) {
  const { user } = useAppSelector((state) => state.auth);

  // If no user is logged in, don't show anything
  if (!user) {
    return showFallback ? <>{fallback}</> : null;
  }

  let hasAccess = false;

  // Check single permission
  if (permission) {
    hasAccess = hasPermission(user.role, user.customPermissions, permission);
  }
  // Check if user has ANY of the permissions
  else if (anyPermissions && anyPermissions.length > 0) {
    hasAccess = hasAnyPermission(user.role, user.customPermissions, anyPermissions);
  }
  // Check if user has ALL of the permissions
  else if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAllPermissions(user.role, user.customPermissions, allPermissions);
  }
  // If no permissions specified, allow access
  else {
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return showFallback ? <>{fallback}</> : null;
}

// Hook for checking permissions in components
export function usePermission(permission: Permission): boolean {
  const { user } = useAppSelector((state) => state.auth);
  if (!user) return false;
  return hasPermission(user.role, user.customPermissions, permission);
}

export function useAnyPermission(permissions: Permission[]): boolean {
  const { user } = useAppSelector((state) => state.auth);
  if (!user) return false;
  return hasAnyPermission(user.role, user.customPermissions, permissions);
}

export function useAllPermissions(permissions: Permission[]): boolean {
  const { user } = useAppSelector((state) => state.auth);
  if (!user) return false;
  return hasAllPermissions(user.role, user.customPermissions, permissions);
}
